import * as am5 from '@amcharts/amcharts5';
import { Grid, MenuItem, Paper, Select, Typography } from '@mui/material';
import i18next from 'i18next';
import { useCallback, useContext, useEffect, useState } from 'react';
import { hidePieSliceLabel } from 'services/common/ChartUtils';
import useStyles from 'style/js-style/containers/dashboard/TabStyle';

import useSelectedAccountId from 'components/common/hooks/useSelectedAccountId';
import { useRef } from 'react';
import { isAbortError } from 'services/common/ApiUtils';
import { ERROR } from 'services/common/Constants';
import { formatNumber } from 'services/common/Utils';
import { Context } from 'states/Store';
import RiskScorePieChart from '../RiskScorePieChart';
import RiskSubScoresPieChart from '../RiskSubScoresPieChart';
import { getCreditsafeScoresCount } from 'services/edge/CreditsafeService';
import { CreditsafeIndicators, getCreditsafeColorDependingOnScore } from 'services/common/CreditsafeUtils';
import { theme } from 'style/globalStyle';

function TabDashboardCreditsafe() {
  const { classes } = useStyles();
  const [state, dispatch] = useContext(Context);
  const [mappedScoreCount, setMappedScoreCount] = useState({});
  const [selectedScoreType, setSelectedScoreType] = useState(CreditsafeIndicators.INTERNATIONAL_SCORE);
  const accountId = useSelectedAccountId();

  const abortCtrlRef = useRef();

  const fetchScoreCount = useCallback(
    (query) => {
      if (abortCtrlRef.current) {
        abortCtrlRef.current.abort();
      }

      abortCtrlRef.current = new AbortController();
      const signal = abortCtrlRef.current.signal;

      if (accountId && state.account) {
        (async () => {
          try {
            const res = await getCreditsafeScoresCount(
              accountId,
              query,
              null,
              signal
            );

            let mappedScoresCountsObj = {};
            for (const scoreCounts of res.content) {
              // Selected score from result
              const selectedScore = scoreCounts.score;
              // Selected score counts from result
              const selectedCounts = scoreCounts.scoreCounts;

              // Total tps for this score
              const total = selectedCounts
                ?.map((d) => d.count)
                .reduce((partialSum, a) => partialSum + a, 0);

              mappedScoresCountsObj[selectedScore] = {
                data: selectedCounts
                  .map((range) => {
                    let color = getCreditsafeColorDependingOnScore(selectedScore,
                          range.scoreLabel.toUpperCase()
                        );
                    return {
                    id: range.scoreLabel.toUpperCase(),
                    itemLabel: i18next.t(`creditsafe.dashboard.scores.${selectedScore}.${range.scoreLabel.toUpperCase()}`),
                    type: `creditsafe_${selectedScore}`,
                    value: range.count,
                    formattedValue: formatNumber(range.count),
                    settings: {
                      fill: am5.color(
                        color
                      ),
                    },
                    labelSettings: {
                      forceHidden: hidePieSliceLabel(range.count, total),
                    },
                  };}),
                total: formatNumber(total),
              };
            }
            setMappedScoreCount(mappedScoresCountsObj);
          } catch (e) {
            if (!isAbortError(e)) {
              console.error('error', e);
              dispatch({
                type: 'ALERT',
                alert: { type: ERROR, msg: e.message },
              });
            }
          }
        })();
      }
    },
    [accountId, dispatch, state.account]
  );

  // Build filters query from graph filters
  useEffect(() => {
    let queryObj = {};

    if (state.graphFilters?.pie?.length > 0 && state.graphFilters?.pie?.type === "creditsafe_nationalScoreValue") {
      queryObj = state.graphFilters.pie.reduce((result, item) => {
        const { id, type } = item;
        if (!result[type]) {
          result[type] = [];
        }
        result[type].push(id);
        return result;
      }, {});
    }
    fetchScoreCount(queryObj);

    return () => {
      abortCtrlRef.current?.abort();
    };
  }, [fetchScoreCount, state.graphFilters?.pie]);

  const getCreditsafeSubScores = () => {
    return Object.keys(mappedScoreCount)
      ?.filter((score) => score !== CreditsafeIndicators.INTERNATIONAL_SCORE 
      && score !== CreditsafeIndicators.NATIONAL_SCORE 
      && score !== CreditsafeIndicators.CREDIT_LIMIT
      && score !== CreditsafeIndicators.PAYMENT_BEHAVIOR)
      .map((score) => ({
        scoreLabel: i18next.t(`creditsafe.scores.${score}`),
        data: mappedScoreCount?.[score],
      }));
  };

  const handleChange = (select) =>  setSelectedScoreType(select);

  return (
    <Paper elevation={0} className={classes.paperContainer}>
      <Grid container spacing={2} style={{ height: '100%' }}>
        <Grid item xs={4} lg={4} key="creditsafe-global">
          <Paper className={classes.graphPaper}>
            <Typography variant="h3" className={classes.graphAreaTitle}>
              <div style={{ display: 'flex' }}>
                <span>{i18next.t(`creditsafe.dashboard.title`)}</span>
              </div>
            </Typography>
            <Select
              value={selectedScoreType}
              onChange={e => handleChange(e.target.value)}
              sx={{ paddingRight: "15px", position: "relative", bottom: "10px", backgroundColor: "white" }}
            >
              <MenuItem key={CreditsafeIndicators.INTERNATIONAL_SCORE} value={CreditsafeIndicators.INTERNATIONAL_SCORE}>
                <span className={classes.swithMapLabel}>
                  {i18next.t("creditsafe.scores.internationalScoreShort")}
                </span>
              </MenuItem>

              <MenuItem key={CreditsafeIndicators.NATIONAL_SCORE} value={CreditsafeIndicators.NATIONAL_SCORE}>
                <span className={classes.swithMapLabel}>
                  {i18next.t("creditsafe.scores.nationalScoreValueShort")}
                </span>
              </MenuItem>
            </Select>
            
              <RiskScorePieChart
                subTab={'creditsafe'}
                scoreCount={mappedScoreCount?.[selectedScoreType]}
                disableClick={selectedScoreType === CreditsafeIndicators.NATIONAL_SCORE}
              />
              {selectedScoreType === CreditsafeIndicators.NATIONAL_SCORE && (
                <Typography variant="h6" style={{color: theme.palette.grey.grey600, textAlign: 'center'}}>Attention, ce graphique ne concerne que les tiers français</Typography>
              )}
            
          </Paper>
        </Grid>
        <Grid item xs={8} lg={8} key="creditsafe-subscores">
          <RiskSubScoresPieChart
            title={i18next.t(`creditsafe.dashboard.subScoresTitle`)}
            riskLabel="creditsafe"
            subScores={getCreditsafeSubScores()}
          />
        </Grid>
      </Grid>
    </Paper>
  );
}

export default TabDashboardCreditsafe;
